import React, { FunctionComponent, useEffect } from 'react'
import { Trans, useTranslation } from '../../utils/convergence'
import { Text, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { TermLink } from '../TermLink'
import {
  setConfirmTermsViewed,
  useAppDispatch,
  useAppRouter,
  useConsumerAmount,
  useIsAfterpayAppGiftCardFlow,
  useLoanAgreementDocumentId,
  usePaymentAuthorisationDocumentId,
} from '../../state'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useMoney } from '../Money/useMoney'
import { useGetIsAutoPayAgreed } from '../AutopayToggle/hooks'
import { buildTranslationKey, TranslationSegment } from '../../utils/locales'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import styles from './style.module.css'

interface TermsProps {
  setDocumentIdToShow: (documentId: string, isTilDisclosure?: boolean) => void
}

export const Terms: FunctionComponent<TermsProps> = ({ setDocumentIdToShow }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const autopayAgreed = useGetIsAutoPayAgreed()
  const paymentAuthorisationDocumentId = usePaymentAuthorisationDocumentId()
  const loanAgreementDocumentId = useLoanAgreementDocumentId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const estimatedCartAmount = useConsumerAmount()
  const dispatch = useAppDispatch()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const { formatMoney } = useMoney({
    locale,
    currency: estimatedCartAmount?.currency,
  })
  const context = autopayAgreed ? 'autopayOn' : 'autopayOff'

  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const components = {
    autoPayAuthorization: (
      <TermLink
        key='til-autopay-authorisation'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='auto-payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_PAYMENT_AUTHORISATION_LINK)
          setDocumentIdToShow(paymentAuthorisationDocumentId)
        }}
      />
    ),
    truthInLendingDisclosure: (
      <TermLink
        key='til-disclosure'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK)
          setDocumentIdToShow(loanAgreementDocumentId, true)
        }}
      />
    ),
    loanAgreement: (
      <TermLink
        key='til-loan-agreement'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='loan-agreement-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_LOAN_AGREEMENT_LINK)
          setDocumentIdToShow(loanAgreementDocumentId)
        }}
      />
    ),
    FEBPolicy: (
      <TermLink
        key='til-feb-privacy-policy'
        href={t('financeFee:til.febPrivacyPolicyUrl')}
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='feb-privacy-policy-link'
        target='_blank'
      />
    ),
    br: <br />,
  }

  const termSegment: TranslationSegment = {
    contexts: [[isAfterpayAppGiftCardFlow, 'terms_giftCard']],
    default: 'terms',
  }

  const i18nKey = buildTranslationKey({ namespace: 'financeFee', segments: ['til', termSegment] })

  useEffect(() => {
    const termsKey = `${i18nKey}_${context}`
    dispatch(setConfirmTermsViewed(termsKey))
  }, [i18nKey, context])

  return (
    <div className={designLanguageStyles.termsContainer}>
      <MaskedElement actionName='Terms And Conditions'>
        <Text color='Gray40' size={isArcadeDesignLanguage ? 'XXS' : 'XS'} testNameSpace='terms-conditions' fontFace='CashSansMono'>
          <Trans
            i18nKey={i18nKey}
            components={components}
            context={context}
            values={{
              amount: formatMoney(estimatedCartAmount?.amount),
            }}
          />
        </Text>
      </MaskedElement>
      <TrackOnMount eventName={TrackingEvent.VIEWED_CHECKOUT_SUMMARY_TERMS} eventProperties={{ autopayAgreed, termsViewed: i18nKey }} />
    </div>
  )
}
